import React, { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import { ProjectPropertyCreateInput } from "../../../api-models/request/ProjectPropertyInput";
import PageLayout from "../../_layout/PageLayout";
import { SettingsMenuItemsPages, SettingsMenuItemsModel } from "../SettingsMenuItems";
import Form from "../../../components/form/Form";
import { AddOrganizationProjectProperties } from "../../../services/authorization/Permissions";
import { EditProjectPropertyLabel, EditProjectPropertyType } from "./components/DataComponents";
import { DataSection, SubSection } from "../../../components/data-components/sections/DataSections";
import "./ProjectProperties.scss";
import { ProjectPropertiesStorageModel } from "../../../services/organization/ProjectPropertyStorage";
import { api } from "../../../services/api/ApiService";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [settingsMenuItems] = useState(() =>
    SettingsMenuItemsModel.create({ menuItemPage: SettingsMenuItemsPages.CreateProjectProperty })
  );

  const [projectPropertyStorage] = useState(() =>
    ProjectPropertiesStorageModel.create({}, { api })
  );

  const menuItems = {
    staticItems: settingsMenuItems.getStaticItems(),
  };

  const initialValues = useMemo(() => {
    return {
      label: "",
      type: {
        id: 0,
        name: "",
      },
      customOptions: "",
    };
  }, []);

  const goToViewProjectPropertiesPage = useCallback(() => {
    history.push("/settings/project-properties");
  }, [history]);

  const goToViewProjectPropertiesList = useCallback(
    () => history.push("/settings/project-properties"),
    [history]
  );

  const handleSubmit = async (values: any, formActions: any) => {
    const projectProperty: ProjectPropertyCreateInput = {
      label: values.label.trim(),
      type: Number(values.type.id),
      customOptions: values.customOptions ? values.customOptions : "",
    };
    try {
      await projectPropertyStorage.createProjectProperty(projectProperty);
      warningLeavePageService.setIsDirty(false);
      goToViewProjectPropertiesList();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(values, formActions);
      applicationError.withErrorMessage("default-error.custom-project-properties.create");
      handleErrorInSnackbar(applicationError);
    }
  };

  return (
    <div>
      <PageLayout
        accessWithOneOfThePermissions={[AddOrganizationProjectProperties]}
        id="create-project-properties"
        titleKey="custom-project-properties.edit.title"
        menuItems={menuItems}
      >
        <Form
          formikOptions={{
            initialValues: initialValues,
            onSubmit: handleSubmit,
            validateOnBlur: false,
            validateOnChange: false,
          }}
          onCancel={goToViewProjectPropertiesPage}
        >
          {({ values, setFieldValue }) => (
            <>
              <DataSection labelKey="custom-project-properties.property.details">
                <SubSection>
                  <EditProjectPropertyLabel label={values.label} />
                </SubSection>
                <SubSection>
                  <EditProjectPropertyType
                    type={values.type}
                    customOptions={values.customOptions}
                    setFieldValue={setFieldValue}
                  />
                </SubSection>
              </DataSection>
            </>
          )}
        </Form>
      </PageLayout>
    </div>
  );
});
